import { useEffect, useState } from "react";

export type RealDeviceType = "mobile" | "tablet" | "desktop";

interface NavigatorWithUserAgentData extends Navigator {
  userAgentData?: {
    mobile?: boolean;
  };
}

/**
 * Detects the actual physical device type (mobile, tablet, or desktop/laptop)
 * by examining:
 * 1. Hardware touch capabilities (maxTouchPoints)
 * 2. Operating system / platform signatures (iPadOS MacIntel emulation, Android)
 * 3. User agent & Client Hints (navigator.userAgentData?.mobile)
 * 4. Physical screen dimensions (screen.width & screen.height)
 */
export function getPhysicalDeviceType(): RealDeviceType {
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return "desktop";
  }

  const ua = navigator.userAgent;
  const nav = navigator as NavigatorWithUserAgentData;

  // 1. iPad & iPadOS detection:
  // Modern iPadOS Safari reports navigator.platform as "MacIntel", but has multi-touch.
  // Standard MacBooks/iMacs have maxTouchPoints === 0.
  const isIPad =
    /ipad/i.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

  // 2. Android Tablet detection:
  // Android convention: Android smartphones have "Mobile" in the UA, tablets do not.
  const isAndroidTablet = /android/i.test(ua) && !/mobile/i.test(ua);

  // 3. Explicit tablet keywords
  const isTabletUA = /tablet|playbook|silk/i.test(ua);

  if (isIPad || isAndroidTablet || isTabletUA) {
    return "tablet";
  }

  // 4. Smartphone / Mobile detection:
  // iPhone, iPod, Android Mobile, Windows Phone, Opera Mini, or Chromium Client Hints
  const isMobileUA =
    /iphone|ipod|android.*mobile|blackberry|iemobile|opera mini/i.test(ua) ||
    Boolean(nav.userAgentData?.mobile);

  if (isMobileUA) {
    return "mobile";
  }

  // 5. Hardware physical screen dimension check:
  // Evaluates device display hardware for touch devices
  if (navigator.maxTouchPoints > 0 && window.screen) {
    const minDim = Math.min(window.screen.width, window.screen.height);
    const maxDim = Math.max(window.screen.width, window.screen.height);

    // Phones typically have portrait width < 600px
    if (minDim < 600 && maxDim < 1000) {
      return "mobile";
    }
    // Tablets typically have portrait width between 600px and 1024px
    if (minDim >= 600 && maxDim <= 1366) {
      return "tablet";
    }
  }

  // 6. Otherwise, it is a physical laptop or desktop computer
  return "desktop";
}

export interface CandidateDeviceStatus {
  deviceType: RealDeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  isWindowTooSmall: boolean;
  isMobileOrTablet: boolean;
}

/**
 * Custom hook that identifies whether the candidate is physically on a mobile,
 * tablet, or laptop/desktop, and listens to browser events (resize, orientation)
 * to react in real time.
 */
export function useCandidateDeviceCheck(): CandidateDeviceStatus {
  const [deviceType, setDeviceType] = useState<RealDeviceType>(() =>
    getPhysicalDeviceType(),
  );
  const [isWindowTooSmall, setIsWindowTooSmall] = useState<boolean>(() =>
    typeof window !== "undefined" ? window.innerWidth < 1024 : false,
  );

  useEffect(() => {
    const handleDeviceCheck = () => {
      setDeviceType(getPhysicalDeviceType());
      setIsWindowTooSmall(window.innerWidth < 1024);
    };

    // Initial check
    handleDeviceCheck();

    // 1. Browser window resize event
    window.addEventListener("resize", handleDeviceCheck);

    // 2. Screen orientation change event
    window.screen?.orientation?.addEventListener?.("change", handleDeviceCheck);

    // 3. Viewport width breakpoint event
    const mql = window.matchMedia("(max-width: 1023px)");
    mql.addEventListener?.("change", handleDeviceCheck);

    return () => {
      window.removeEventListener("resize", handleDeviceCheck);
      window.screen?.orientation?.removeEventListener?.(
        "change",
        handleDeviceCheck,
      );
      mql.removeEventListener?.("change", handleDeviceCheck);
    };
  }, []);

  const isMobile = deviceType === "mobile";
  const isTablet = deviceType === "tablet";
  const isDesktop = deviceType === "desktop";

  // Blocked if physically on mobile or tablet, or if on desktop with window < 1024px
  const isMobileOrTablet = isMobile || isTablet || isWindowTooSmall;

  return {
    deviceType,
    isMobile,
    isTablet,
    isDesktop,
    isWindowTooSmall,
    isMobileOrTablet,
  };
}
